import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = 'https://www.dionmageeevents.com';
const DEFAULT_OG = `${SITE}/og-image.jpg`;

/**
 * Reusable per-page SEO component.
 *
 * Props:
 *  - title, description, path, image, type (see above)
 *  - article: optional { headline, datePublished, author?, dateModified?, section? }
 *             When provided, adds JSON-LD Article schema for Google rich results.
 */
const SEO = ({ title, description, path = '/', image, type = 'website', article }) => {
  const url = path === '/' ? SITE : `${SITE}${path}`;
  const ogImage = image ? (image.startsWith('http') ? image : `${SITE}${image}`) : DEFAULT_OG;

  const articleSchema = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        headline: article.headline || title,
        description,
        image: [ogImage],
        datePublished: article.datePublished,
        dateModified: article.dateModified || article.datePublished,
        author: {
          '@type': 'Person',
          name: article.author || 'Sir Dion Magee',
          url: SITE,
        },
        publisher: {
          '@type': 'Organization',
          name: 'Dion Magee Events & Weddings',
          logo: { '@type': 'ImageObject', url: `${SITE}/dm-logo.png` },
        },
        ...(article.section && { articleSection: article.section }),
      }
    : null;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Dion Magee Events & Weddings" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Article JSON-LD (rich results) */}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
